import { useState, useRef, useCallback, useEffect } from "react";
import type { ApiClient } from "@/lib/api/chatApi";
import { computeContextKey, renderTemplate, getStoredThread, saveThread, removeThread } from "@/lib/context";

/* ── Types ── */

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  /** Thumbnail preview data (not persisted across reloads) */
  filePreviewUrl?: string;
  fileName?: string;
  fileIsImage?: boolean;
}

interface UseWidgetChatParams {
  api: ApiClient;
  agentId: string;
  widgetUid: string;
  context: Record<string, string>;
  contextPromptTemplate: string | null;
  threadTtlHours: number;
  userEmail: string;
}

/* ── SSE parser (simplified from ChatRuntime) ── */

interface SSECallbacks {
  onText: (text: string) => void;
  onToolCall: (name: string) => void;
  onToolEnd: (id: string) => void;
  onEnd: (data: { thread_id?: string; trace_id?: string }) => void;
}

async function readSSE(
  body: ReadableStream<Uint8Array>,
  callbacks: SSECallbacks,
  abortSignal?: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (abortSignal?.aborted || done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;

        let eventType = "";
        let dataStr = "";
        for (const line of trimmed.split("\n")) {
          if (line.startsWith("event: ")) eventType = line.slice(7).trim();
          else if (line.startsWith("data: ")) dataStr = line.slice(6);
        }

        if (!dataStr || eventType === "metadata") continue;
        if (eventType === "end") {
          try {
            callbacks.onEnd(JSON.parse(dataStr));
          } catch {
            /* skip */
          }
          continue;
        }

        if (eventType === "error") {
          try {
            const errData = JSON.parse(dataStr);
            callbacks.onText(errData.message || "An error occurred");
          } catch { /* skip */ }
          continue;
        }

        if (eventType === "messages") {
          try {
            const data = JSON.parse(dataStr);
            const chunk = Array.isArray(data) ? data[0] : data;

            // Track tool calls starting
            if (chunk?.tool_calls?.length > 0) {
              for (const tc of chunk.tool_calls) {
                const name = tc.name || tc.function?.name;
                if (name) callbacks.onToolCall(name);
              }
              continue;
            }

            // Track tool results (tool finished)
            if (chunk?.type === "tool") {
              const callId = chunk.tool_call_id;
              if (callId) callbacks.onToolEnd(callId);
              continue;
            }

            // Skip non-AI messages
            if (chunk?.type === "human") continue;
            if (chunk?.type === "system") continue;

            const content = chunk?.content;
            if (content && typeof content === "string") {
              callbacks.onText(content);
            }
          } catch {
            /* skip */
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/* ── Hook ── */

export function useWidgetChat({
  api,
  agentId,
  widgetUid,
  context,
  contextPromptTemplate,
  threadTtlHours,
  userEmail,
}: UseWidgetChatParams) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTools, setActiveTools] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const ctxKeyRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const initializedRef = useRef(false);
  // Track whether this is the first user message in the thread (for context injection)
  const isFirstMessageRef = useRef(true);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize: load existing thread if available
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    (async () => {
      const key = await computeContextKey(userEmail, agentId, context);
      ctxKeyRef.current = key;

      const entry = getStoredThread(key, threadTtlHours);
      if (entry) {
        setThreadId(entry.threadId);
        isFirstMessageRef.current = false;
        // Load existing messages
        try {
          const threadMessages = await api.getThreadMessages(
            entry.threadId,
            agentId,
          );
          const restored: ChatMessage[] = [];
          for (const msg of threadMessages) {
            const text =
              typeof msg.content === "string"
                ? msg.content
                : Array.isArray(msg.content)
                  ? (
                      msg.content as Array<{ type: string; text?: string }>
                    )
                      .filter(
                        (p) =>
                          typeof p === "object" &&
                          p !== null &&
                          p.type === "text",
                      )
                      .map((p) => p.text ?? "")
                      .join("")
                  : "";
            if (text) {
              restored.push({
                role: msg.role === "user" ? "user" : "assistant",
                content: text,
              });
            }
          }
          if (restored.length > 0) {
            setMessages(restored);
          }
        } catch {
          // Thread may have expired server-side
          removeThread(key);
          setThreadId(null);
          isFirstMessageRef.current = true;
        }
      }
    })();
  }, [api, agentId, context, threadTtlHours, userEmail]);

  // Shared logic: ensure thread exists, save to localStorage
  const ensureThread = useCallback(async (): Promise<string> => {
    if (threadId) return threadId;
    const { thread_id } = await api.createThread();
    setThreadId(thread_id);
    if (ctxKeyRef.current) saveThread(ctxKeyRef.current, thread_id);
    return thread_id;
  }, [api, threadId]);

  // Shared logic: build context message for first interaction
  // Skip context injection if no context values were provided (widget without params = normal chat)
  const getContextMessage = useCallback((): string => {
    if (!isFirstMessageRef.current || !contextPromptTemplate) return "";
    if (Object.keys(context).length === 0) return "";
    return renderTemplate(contextPromptTemplate, context);
  }, [context, contextPromptTemplate]);

  // Shared logic: stream SSE response and update messages
  const streamResponse = useCallback(
    async (response: Response, currentThreadId: string, abort: AbortController) => {
      if (!response.body) throw new Error("No response body");

      let fullText = "";
      setActiveTools([]);

      await readSSE(
        response.body,
        {
          onText: (chunk) => {
            fullText += chunk;
            setActiveTools([]);
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: "assistant", content: fullText };
              return updated;
            });
          },
          onToolCall: (name) => {
            setActiveTools((prev) => prev.includes(name) ? prev : [...prev, name]);
          },
          onToolEnd: () => {
            setActiveTools((prev) => prev.slice(1));
          },
          onEnd: (data) => {
            setActiveTools([]);
            if (data.thread_id && data.thread_id !== currentThreadId) {
              setThreadId(data.thread_id);
              if (ctxKeyRef.current) saveThread(ctxKeyRef.current, data.thread_id);
            }
          },
        },
        abort.signal,
      );

      setActiveTools([]);
      if (!fullText) {
        setMessages((prev) => prev.slice(0, -1));
      }
    },
    [],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const files = pendingFiles;
      if (!text.trim() && files.length === 0) return;
      if (isLoading) return;

      let filePreviewUrl: string | undefined;
      let fileName: string | undefined;
      let fileIsImage: boolean | undefined;
      if (files.length > 0) {
        const imageFile = files.find(f => f.type.startsWith("image/"));
        const displayFile = imageFile || files[0];
        fileName = files.length === 1 ? displayFile.name : `${files.length} archivos`;
        fileIsImage = !!imageFile;
        if (imageFile) {
          filePreviewUrl = URL.createObjectURL(imageFile);
        }
      }
      const displayText = files.length > 0 ? (text.trim() || "") : text;
      setMessages((prev) => [...prev, { role: "user", content: displayText, filePreviewUrl, fileName, fileIsImage }]);
      setInput("");
      setPendingFiles([]);
      setIsLoading(true);
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

      const abort = new AbortController();
      abortRef.current = abort;

      try {
        const currentThreadId = await ensureThread();

        let response: Response;

        if (files.length > 0) {
          // File upload path
          let msgText = text.trim() || "";
          const ctxMsg = getContextMessage();
          if (ctxMsg) msgText = ctxMsg + (msgText ? "\n\n" + msgText : "");
          isFirstMessageRef.current = false;

          // El uid viaja como campo del multipart (la rama de texto de abajo lo
          // manda en `config.metadata`): es lo que hace que el backend trace
          // `source:widget` y se ahorre el formulario sugerido tambien al subir.
          response = await api.uploadFiles({
            files,
            assistantId: agentId,
            message: msgText,
            threadId: currentThreadId,
            widgetUid,
          });
        } else {
          // Text-only path
          const langchainMessages: { type: string; content: string }[] = [];
          const ctxMsg = getContextMessage();
          if (ctxMsg) langchainMessages.push({ type: "system", content: ctxMsg });
          isFirstMessageRef.current = false;
          langchainMessages.push({ type: "human", content: text });

          response = await api.sendMessage({
            assistantId: agentId,
            threadId: currentThreadId,
            input: { messages: langchainMessages },
            streamMode: ["messages", "updates"],
            config: { metadata: { widget_uid: widgetUid } },
          });
        }

        await streamResponse(response, currentThreadId, abort);
      } catch (err) {
        if (!(err instanceof DOMException && err.name === "AbortError")) {
          console.error("Widget chat error:", err);
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === "assistant" && !last.content) return prev.slice(0, -1);
            return prev;
          });
        }
      } finally {
        setIsLoading(false);
        abortRef.current = null;
      }
    },
    [api, agentId, widgetUid, pendingFiles, ensureThread, getContextMessage, streamResponse, isLoading],
  );

  const attachFiles = useCallback((newFiles: File[]) => {
    setPendingFiles((prev) => {
      const combined = [...prev, ...newFiles];
      return combined.slice(0, 5);
    });
  }, []);

  const removeFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleNewConversation = useCallback(async () => {
    abortRef.current?.abort();

    if (ctxKeyRef.current) removeThread(ctxKeyRef.current);
    setThreadId(null);
    setMessages([]);
    setInput("");
    setIsLoading(false);
    isFirstMessageRef.current = true;
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return {
    messages,
    input,
    isLoading,
    activeTools,
    threadId,
    setInput,
    sendMessage,
    pendingFiles,
    attachFiles,
    removeFile,
    handleKeyDown,
    handleNewConversation,
    messagesEndRef,
  };
}
